import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Gauge, Database, Activity, MonitorSmartphone, Video, Printer,
  Mail, Server, RadioTower, ShieldCheck, CalendarClock,
} from 'lucide-react';
import api from '../../utils/api';

export const meta = {
  id: 'ops_status',
  category: 'system',
  titleKey: 'dashboard.widgets.opsStatus',
  descriptionKey: 'dashboard.widgets.opsStatusDesc',
  defaultSize: 'md',
  minRole: 'manager',
  // Specific agents Andy wants to always watch the ops board even though they're
  // not managers (field techs). allowUserIds bypasses minRole for these users.
  // Beto is already a manager (passes minRole) — listed for explicitness.
  allowUserIds: [
    '312f7445-a9cb-4acd-9765-f28c707d8b13', // Beto Nasimoff
    'ae743174-ba1f-4fc0-bb2d-6a2e1674a536', // Ezequiel Ortiz
    '6e5737f2-351d-4c99-9075-9d81be743a72', // Gonzalo Paltenghi
  ],
  icon: Gauge,
};

// Visual vocabulary for the four status levels.
const STATUS = {
  ok:   { color: '#22c55e', label: 'ok' },
  warn: { color: '#f59e0b', label: 'warn' },
  crit: { color: '#ef4444', label: 'crit' },
  idle: { color: '#94a3b8', label: 'idle' },
};

// Per-signal icon + deep-link target (paths verified against the sidebar nav).
const SIGNAL_META = {
  backups:    { icon: Database,          path: '/backup-monitor' },
  monitoring: { icon: Activity,          path: '/monitor' },
  endpoints:  { icon: MonitorSmartphone, path: '/monitoring' },
  cctv:       { icon: Video,             path: '/cctv' },
  printers:   { icon: Printer,           path: '/supplies' },
  email_auth: { icon: ShieldCheck,       path: '/monitor' },
  expiries:   { icon: CalendarClock,     path: '/monitor' },
  email:      { icon: Mail,              path: '/comms-monitor' },
  vps:        { icon: Server,            path: '/vps-health' },
  probes:     { icon: RadioTower,        path: '/monitor' },
};

// Relative "hace Xs / Xm" for the live footer.
function ago(iso, t) {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return t('dashboard.widgets.opsStatusAgoSec', { n: s });
  return t('dashboard.widgets.opsStatusAgoMin', { n: Math.floor(s / 60) });
}

function Header({ t, overall }) {
  const ov = STATUS[overall] || STATUS.idle;
  const ovLabel = overall === 'ok' ? t('dashboard.widgets.opsStatusAllOk')
    : overall === 'idle' ? '—'
    : t(`dashboard.widgets.opsStatusLevel.${overall}`);
  return (
    <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Gauge size={16} /> {t('dashboard.widgets.opsStatus')}
      </div>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700,
        padding: '2px 9px', borderRadius: 999, background: `${ov.color}1f`, color: ov.color,
      }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: ov.color,
          boxShadow: `0 0 0 3px ${ov.color}33` }} />
        {ovLabel}
      </span>
    </div>
  );
}

function Tile({ sig, t, onClick }) {
  const sm = SIGNAL_META[sig.key] || {};
  const Icon = sm.icon || Activity;
  const st = STATUS[sig.status] || STATUS.idle;
  return (
    <button
      type="button"
      onClick={onClick}
      title={t(`dashboard.widgets.opsStatusSignals.${sig.key}`)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', cursor: onClick ? 'pointer' : 'default',
        padding: '10px 12px', borderRadius: 12, border: '1px solid var(--c-border)',
        background: `linear-gradient(135deg, ${st.color}14, transparent 70%)`,
        position: 'relative', width: '100%',
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = st.color; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--c-border)'; }}
    >
      <span style={{
        flexShrink: 0, width: 32, height: 32, borderRadius: 9, display: 'grid', placeItems: 'center',
        background: `${st.color}22`, color: st.color,
      }}>
        <Icon size={17} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 11, color: 'var(--c-text-muted)', fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: 0.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {t(`dashboard.widgets.opsStatusSignals.${sig.key}`)}
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--c-text)', display: 'flex', alignItems: 'baseline', gap: 6 }}>
          {sig.value || '—'}
          {sig.bad > 0 && !sig.info && (
            <span style={{ fontSize: 11, fontWeight: 700, color: st.color }}>· {sig.bad}</span>
          )}
        </div>
        {/* Lo crónico va en SU PROPIA línea. Apretado contra los números se partía en dos
            —"343/356 · +1 / 6 chronic"— y dejaba de leerse como lo que es. Apagado y aparte:
            sigue a la vista para limpiarlo alguna vez, pero no compite con lo que se rompió
            recién, que es lo único accionable hoy. */}
        {sig.cronicos > 0 && (
          <div title={t('dashboard.widgets.opsChronicTip')}
               style={{ fontSize: 10, fontWeight: 600, color: 'var(--c-text-muted)',
                        marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            +{sig.cronicos} {t('dashboard.widgets.opsChronic')}
          </div>
        )}
      </div>
      {/* status dot, top-right — pulses when critical to draw the eye */}
      <span style={{ position: 'absolute', top: 8, right: 8, width: 8, height: 8, borderRadius: '50%',
        background: st.color,
        boxShadow: sig.status === 'crit' ? `0 0 0 3px ${st.color}33` : 'none',
        animation: sig.status === 'crit' ? 'opsPulse 1.5s ease-in-out infinite' : 'none' }} />
    </button>
  );
}

export default function OpsStatusWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['ops-status'],
    queryFn: () => api.get('/dashboard/ops-status').then(r => r.data),
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
  });

  if (isLoading) {
    return (
      <div className="card" style={{ height: '100%' }}>
        <Header t={t} overall="idle" />
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>…</div>
      </div>
    );
  }

  const signals = (data && Array.isArray(data.signals)) ? data.signals : [];
  // Count the signals that need attention (excludes the info-only tiles).
  const alertCount = signals.filter(s => !s.info && (s.status === 'crit' || s.status === 'warn')).length;

  return (
    <div className="card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <style>{`@keyframes opsPulse { 0%,100% { box-shadow: 0 0 0 2px var(--ops-ring,rgba(239,68,68,.25)); } 50% { box-shadow: 0 0 0 5px transparent; } }`}</style>
      <Header t={t} overall={data?.overall || 'idle'} />
      <div style={{
        padding: 12, display: 'grid', gap: 8,
        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      }}>
        {signals.map(sig => {
          const path = SIGNAL_META[sig.key]?.path;
          return (
            <Tile key={sig.key} sig={sig} t={t} onClick={path ? () => navigate(path) : undefined} />
          );
        })}
        {signals.length === 0 && (
          <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>—</div>
        )}
      </div>
      {/* live footer: last refresh + how many signals need attention */}
      <div style={{
        marginTop: 'auto', padding: '8px 14px', borderTop: '1px solid var(--c-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 11, color: 'var(--c-text-muted)',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e' }} />
          {t('dashboard.widgets.opsStatusUpdated', { ago: ago(data?.generated_at, t) })}
        </span>
        <span>{alertCount === 0
          ? t('dashboard.widgets.opsStatusAllOk')
          : t('dashboard.widgets.opsStatusAlertCount', { n: alertCount })}</span>
      </div>
    </div>
  );
}
